import { isLeavingPage as isLeavingPageDefault } from '@/lib/pageNavigation'

const PRELOAD_ERROR_EVENT = 'vite:preloadError'
const LAST_RELOAD_STORAGE_KEY = 'vite-preload-reload-at'
const RELOAD_COOLDOWN_MS = 60_000

let recoveryReloadPending = false

/**
 * True once a failed chunk load has triggered a recovery reload. Vite resolves the
 * failed import with `undefined`, so the code that awaited it (e.g. HeroUI's lazy
 * `import('framer-motion').then((res) => res.domMax)`) throws before the reload
 * replaces the page. Those errors are side effects of the reload, not bugs.
 */
export function isRecoveryReloadPending(): boolean {
	return recoveryReloadPending
}

type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem'>

type PreloadErrorRecoveryOptions = {
	eventTarget?: EventTarget
	storage?: RecoveryStorage
	reload?: () => void
	now?: () => number
	isLeavingPage?: () => boolean
}

/**
 * Reload once when a stale deployment can no longer load one of its chunks.
 * A recent timestamp prevents a persistent network/server failure from causing
 * a reload loop; repeated failures remain unhandled so Sentry can report them.
 *
 * Preventing the event makes Vite resolve the failed import with `undefined`, so the
 * reload must only be used when the page is actually being replaced. While the app is
 * leaving the page, an aborted import is a side effect of that navigation, not a stale
 * chunk: reloading would cancel the navigation, so the error is left untouched.
 */
export function registerPreloadErrorRecovery(
	options: PreloadErrorRecoveryOptions = {}
): () => void {
	const eventTarget = options.eventTarget ?? window
	const storage = options.storage ?? window.sessionStorage
	const reload = options.reload ?? (() => window.location.reload())
	const now = options.now ?? Date.now
	const isLeavingPage = options.isLeavingPage ?? isLeavingPageDefault

	const handlePreloadError: EventListener = (event) => {
		if (isLeavingPage()) return

		const currentTime = now()
		let lastReloadTime: number

		try {
			lastReloadTime = Number(storage.getItem(LAST_RELOAD_STORAGE_KEY) ?? 0)
		} catch {
			// Without persistent storage, reloading could create an infinite loop.
			return
		}

		if (
			Number.isFinite(lastReloadTime) &&
			lastReloadTime > 0 &&
			currentTime - lastReloadTime < RELOAD_COOLDOWN_MS
		) {
			return
		}

		try {
			storage.setItem(LAST_RELOAD_STORAGE_KEY, String(currentTime))
		} catch {
			// Let the original error propagate if the loop guard cannot be saved.
			return
		}

		recoveryReloadPending = true
		event.preventDefault()
		reload()
	}

	eventTarget.addEventListener(PRELOAD_ERROR_EVENT, handlePreloadError)
	return () => eventTarget.removeEventListener(PRELOAD_ERROR_EVENT, handlePreloadError)
}
